/**
 * A single labelled number. Numbers always carry their unit at reduced emphasis — never a bare
 * numeral (UI.md). Colour is never the only carrier of state: an abnormal reading also gets a
 * glyph and a `title`.
 */

import type { ReactNode } from 'react';

export type Tone = 'normal' | 'warn' | 'alarm' | 'ok' | 'dim';

const TONE_COLOUR: Record<Tone, string> = {
  normal: 'var(--ink)',
  warn: 'var(--warn)',
  alarm: 'var(--alarm)',
  ok: 'var(--ok)',
  dim: 'var(--ink-dim)',
};

const TONE_GLYPH: Record<Tone, string> = {
  normal: '',
  warn: '▲',
  alarm: '▲',
  ok: '✓',
  dim: '',
};

export function Readout({
  label,
  value,
  unit,
  tone = 'normal',
  size = 'lg',
  hint,
  onClick,
}: {
  label: string;
  value: string;
  unit?: string;
  tone?: Tone;
  size?: 'lg' | 'sm';
  hint?: string;
  onClick?(): void;
}): JSX.Element {
  const colour = TONE_COLOUR[tone];
  const glyph = TONE_GLYPH[tone];
  const body = (
    <>
      <div className="label" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <span>{label}</span>
        {glyph && (
          <span style={{ color: colour }} aria-hidden="true">
            {glyph}
          </span>
        )}
      </div>
      <div className={size === 'lg' ? 'readout' : 'readout-sm'} style={{ color: colour }}>
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
    </>
  );
  return onClick ? (
    <button
      onClick={onClick}
      title={hint}
      style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', width: '100%' }}
    >
      {body}
    </button>
  ) : (
    <div title={hint}>{body}</div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'block' }}>
      <div className="label" style={{ marginBottom: 3 }}>
        {label}
      </div>
      {children}
    </label>
  );
}
